import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiMoneyDollarCircleLine,
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiUserLine,
  RiFileTextLine,
  RiWalletLine,
  RiHandCoinLine,
  RiCalendarLine,
  RiInformationLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/advanceSalary`;
const STAFF_URL = `${process.env.REACT_APP_API_URL}/admin`;

const fmt = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n ?? 0);

const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
        ${
          type === "success"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
            : "bg-red-50 border border-red-200 text-red-800"
        }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, maxWidth = "max-w-lg" }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${maxWidth} mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const StatCard = ({ label, value, icon: Icon, borderColor }) => (
  <div
    className={`flex items-center gap-4 bg-white border ${borderColor} rounded-xl px-5 py-4 shadow-sm`}
  >
    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
        {label}
      </p>
      <p className="text-lg font-bold text-slate-800 leading-tight">{value}</p>
    </div>
  </div>
);

export default function AdvanceSalary() {
  const [advances, setAdvances] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const [createForm, setCreateForm] = useState({
    staffId: "",
    advanceAmount: "",
    monthlyPayAmount: "",
    reason: "",
    notes: "",
  });

  const [editForm, setEditForm] = useState({
    advanceAmount: "",
    monthlyPayAmount: "",
    reason: "",
    notes: "",
  });

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchAdvances = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/get-all`);
      setAdvances(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch advance records", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${STAFF_URL}/all-users`);
      const all = res.data?.data || res.data || [];
      setStaffList(all.filter((u) => u.userType === "staff"));
    } catch {}
  };

  useEffect(() => {
    fetchAdvances();
    fetchStaff();
  }, []);

  const summary = {
    total: advances.length,
    totalAdvance: advances.reduce((s, x) => s + (x.advanceAmount || 0), 0),
    totalMonthly: advances.reduce((s, x) => s + (x.monthlyPayAmount || 0), 0),
    uniqueStaff: new Set(advances.map((x) => x.staffId?._id || x.staffId)).size,
  };

  const handleCreate = async () => {
    if (
      !createForm.staffId ||
      !createForm.advanceAmount ||
      !createForm.monthlyPayAmount
    ) {
      return notify(
        "Staff, advance amount, and monthly payment are required",
        "error",
      );
    }

    if (parseFloat(createForm.advanceAmount) <= 0) {
      return notify("Advance amount must be greater than 0", "error");
    }

    if (parseFloat(createForm.monthlyPayAmount) <= 0) {
      return notify("Monthly payment amount must be greater than 0", "error");
    }

    if (
      parseFloat(createForm.monthlyPayAmount) >
      parseFloat(createForm.advanceAmount)
    ) {
      return notify(
        "Monthly payment cannot be greater than advance amount",
        "error",
      );
    }

    setActionLoading(true);
    try {
      await axios.post(`${BASE_URL}/create`, {
        staffId: createForm.staffId,
        advanceAmount: parseFloat(createForm.advanceAmount),
        monthlyPayAmount: parseFloat(createForm.monthlyPayAmount),
        reason: createForm.reason,
        notes: createForm.notes,
      });

      notify("Advance salary created successfully");
      fetchAdvances();
      setModal(null);
      setCreateForm({
        staffId: "",
        advanceAmount: "",
        monthlyPayAmount: "",
        reason: "",
        notes: "",
      });
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to create advance salary",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editForm.advanceAmount || !editForm.monthlyPayAmount) {
      return notify("Advance amount and monthly payment are required", "error");
    }

    if (parseFloat(editForm.advanceAmount) <= 0) {
      return notify("Advance amount must be greater than 0", "error");
    }

    if (parseFloat(editForm.monthlyPayAmount) <= 0) {
      return notify("Monthly payment amount must be greater than 0", "error");
    }

    if (
      parseFloat(editForm.monthlyPayAmount) > parseFloat(editForm.advanceAmount)
    ) {
      return notify(
        "Monthly payment cannot be greater than advance amount",
        "error",
      );
    }

    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/update/${selected._id}`, {
        advanceAmount: parseFloat(editForm.advanceAmount),
        monthlyPayAmount: parseFloat(editForm.monthlyPayAmount),
        reason: editForm.reason,
        notes: editForm.notes,
      });
      notify("Advance salary updated successfully");
      fetchAdvances();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to update advance salary",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BASE_URL}/delete/${selected._id}`);
      notify("Advance salary deleted successfully");
      fetchAdvances();
      setModal(null);
    } catch (err) {
      notify(
        err?.response?.data?.message || "Failed to delete advance salary",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openCreate = () => {
    setCreateForm({
      staffId: staffList[0]?._id || "",
      advanceAmount: "",
      monthlyPayAmount: "",
      reason: "",
      notes: "",
    });
    setModal("create");
  };

  const openEdit = (advance) => {
    setSelected(advance);
    setEditForm({
      advanceAmount: advance.advanceAmount,
      monthlyPayAmount: advance.monthlyPayAmount,
      reason: advance.reason || "",
      notes: advance.notes || "",
    });
    setModal("edit");
  };

  const openDelete = (advance) => {
    setSelected(advance);
    setModal("delete");
  };

  const openDetail = (advance) => {
    setSelected(advance);
    setModal("detail");
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const filtered = advances.filter((a) => {
    const name = a.staffId?.name || "";
    const email = a.staffId?.email || "";
    const reason = a.reason || "";
    return (
      name.toLowerCase().includes(search.toLowerCase()) ||
      email.toLowerCase().includes(search.toLowerCase()) ||
      reason.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiHandCoinLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Advance Salary
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Advance Payment Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff or reason…"
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-56 transition"
              />
            </div>

            <button
              onClick={fetchAdvances}
              disabled={loading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Advance
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Records"
            value={summary.total}
            icon={RiFileTextLine}
            borderColor="border-blue-100"
          />
          <StatCard
            label="Unique Staff"
            value={summary.uniqueStaff}
            icon={RiUserLine}
            borderColor="border-purple-100"
          />
          <StatCard
            label="Total Advance Given"
            value={fmt(summary.totalAdvance)}
            icon={RiHandCoinLine}
            borderColor="border-amber-100"
          />
          <StatCard
            label="Monthly Deductions"
            value={fmt(summary.totalMonthly)}
            icon={RiWalletLine}
            borderColor="border-emerald-100"
          />
        </div>

        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiHandCoinLine className="w-4 h-4 text-blue-500" />
              Advance Salary Records
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {[
                    "Staff",
                    "Advance Amount",
                    "Monthly Deduction",
                    "Months to Pay",
                    "Reason",
                    "Created Date",
                    "Actions",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading advances…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiHandCoinLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search
                            ? "No records match your search."
                            : "No advance salary records yet."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((a, i) => {
                    const staff = a.staffId || {};
                    const monthsToPay = Math.ceil(
                      a.advanceAmount / a.monthlyPayAmount,
                    );

                    return (
                      <tr
                        key={a._id || i}
                        className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {staff.profilePic ? (
                              <img
                                src={staff.profilePic}
                                alt={staff.name}
                                className="w-8 h-8 rounded-full object-cover border border-blue-100"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                {(staff.name || "?")[0].toUpperCase()}
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-slate-800 whitespace-nowrap">
                                {staff.name || "—"}
                              </p>
                              <p className="text-[11px] text-slate-400">
                                {staff.position || "Staff"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <span className="font-bold text-slate-800 whitespace-nowrap">
                            {fmt(a.advanceAmount)}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-700 whitespace-nowrap">
                            {fmt(a.monthlyPayAmount)}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                            <RiCalendarLine className="w-3 h-3" />
                            {monthsToPay} month{monthsToPay !== 1 ? "s" : ""}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600 line-clamp-2 max-w-xs">
                            {a.reason || "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {a.createdAt
                              ? new Date(a.createdAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-1.5 flex-wrap">
                            <button
                              onClick={() => openDetail(a)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 font-semibold transition"
                              title="View Details"
                            >
                              <RiFileTextLine className="w-3.5 h-3.5" /> View
                            </button>
                            <button
                              onClick={() => openEdit(a)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                              title="Edit Advance"
                            >
                              <RiEditLine className="w-3.5 h-3.5" /> Edit
                            </button>
                            <button
                              onClick={() => openDelete(a)}
                              className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                              title="Delete Record"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" /> Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Advance Salary
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Select Staff
              </label>
              <div className="relative">
                <RiUserLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300 pointer-events-none" />
                <select
                  value={createForm.staffId}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, staffId: e.target.value }))
                  }
                  className="w-full appearance-none bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-8 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                >
                  {staffList.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name} — {s.position || "Staff"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Advance Amount (₹)
              </label>
              <div className="relative">
                <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={createForm.advanceAmount}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      advanceAmount: e.target.value,
                    }))
                  }
                  placeholder="e.g. 50000"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Monthly Deduction Amount (₹)
              </label>
              <div className="relative">
                <RiWalletLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={createForm.monthlyPayAmount}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      monthlyPayAmount: e.target.value,
                    }))
                  }
                  placeholder="e.g. 5000"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
              {createForm.advanceAmount &&
                createForm.monthlyPayAmount &&
                parseFloat(createForm.monthlyPayAmount) > 0 && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <RiCalendarLine className="w-3 h-3" />
                    Will be paid in{" "}
                    {Math.ceil(
                      parseFloat(createForm.advanceAmount) /
                        parseFloat(createForm.monthlyPayAmount),
                    )}{" "}
                    month(s)
                  </p>
                )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Reason
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={createForm.reason}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, reason: e.target.value }))
                  }
                  rows={2}
                  placeholder="e.g. Medical emergency, personal loan…"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Notes (Optional)
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={createForm.notes}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={2}
                  placeholder="Additional notes…"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>

            <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs text-blue-700 flex items-start gap-1.5">
                <RiInformationLine className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Note:</strong> The monthly deduction amount will be
                  automatically deducted from the staff's salary each month
                  until the advance is fully paid.
                </span>
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Create Advance
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Advance —{" "}
              {selected?.staffId?.name}
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4 mb-6">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Advance Amount (₹)
              </label>
              <div className="relative">
                <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={editForm.advanceAmount}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      advanceAmount: e.target.value,
                    }))
                  }
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Monthly Deduction Amount (₹)
              </label>
              <div className="relative">
                <RiWalletLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                <input
                  type="number"
                  value={editForm.monthlyPayAmount}
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      monthlyPayAmount: e.target.value,
                    }))
                  }
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
              {editForm.advanceAmount &&
                editForm.monthlyPayAmount &&
                parseFloat(editForm.monthlyPayAmount) > 0 && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <RiCalendarLine className="w-3 h-3" />
                    Will be paid in{" "}
                    {Math.ceil(
                      parseFloat(editForm.advanceAmount) /
                        parseFloat(editForm.monthlyPayAmount),
                    )}{" "}
                    month(s)
                  </p>
                )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Reason
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={editForm.reason}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, reason: e.target.value }))
                  }
                  rows={2}
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Notes
              </label>
              <div className="relative">
                <RiFileTextLine className="absolute left-3 top-3 w-4 h-4 text-blue-300" />
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  rows={2}
                  placeholder="Optional notes…"
                  className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition resize-none"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}{" "}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {modal === "detail" && selected && (
        <Modal
          title={
            <>
              <RiFileTextLine className="w-5 h-5 text-blue-500" /> Advance
              Detail — {selected.staffId?.name}
            </>
          }
          onClose={closeModal}
          maxWidth="max-w-md"
        >
          {(() => {
            const a = selected;
            const staff = a.staffId || {};
            const monthsToPay = Math.ceil(a.advanceAmount / a.monthlyPayAmount);

            return (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  {staff.profilePic ? (
                    <img
                      src={staff.profilePic}
                      alt={staff.name}
                      className="w-10 h-10 rounded-full object-cover border border-blue-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                      {(staff.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-800">
                      {staff.name || "—"}
                    </p>
                    <p className="text-xs text-slate-400">
                      {staff.email || ""}
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-bold text-blue-900">
                    Payment Details
                  </h3>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Total Advance Amount
                      </span>
                      <span className="text-base font-bold text-blue-900">
                        {fmt(a.advanceAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Monthly Deduction
                      </span>
                      <span className="text-sm font-bold text-purple-700">
                        {fmt(a.monthlyPayAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700 font-semibold">
                        Payment Duration
                      </span>
                      <span className="text-sm font-bold text-emerald-700">
                        {monthsToPay} month{monthsToPay !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                      Reason
                    </p>
                    <p className="text-sm text-slate-800">{a.reason || "—"}</p>
                  </div>

                  {a.notes && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        Notes
                      </p>
                      <p className="text-sm text-slate-800">{a.notes}</p>
                    </div>
                  )}

                  <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                      Created Date
                    </p>
                    <p className="text-sm font-bold text-slate-800">
                      {a.createdAt
                        ? new Date(a.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>

                  {a.updatedAt && a.updatedAt !== a.createdAt && (
                    <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">
                        Last Updated
                      </p>
                      <p className="text-sm font-bold text-slate-800">
                        {new Date(a.updatedAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end pt-2 border-t border-blue-50">
                  <button
                    onClick={closeModal}
                    className="px-5 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete advance record for{" "}
              <span className="text-red-500">{selected?.staffId?.name}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. The advance salary record will be
              permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}{" "}
                Delete Record
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
